import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

// ─────────────────────────────────────────────────────────────────────────────
// dataGridPrefsSlice — per-grid user preferences (column visibility, etc.)
// ─────────────────────────────────────────────────────────────────────────────
//
// Persisted via redux-persist as part of the `root` slice so users keep their
// column choices across sessions. Each grid is keyed by a stable `gridKey`
// string (e.g. "rules", "users", "roles", "groups", "workstations"). The
// stored value is MUI X DataGrid's `columnVisibilityModel` shape — a plain
// object mapping field name → boolean (false = hidden, true = visible).
//
// When a column is missing from the model the grid treats it as visible by
// default, so we only need to store the user's hide overrides.
// ─────────────────────────────────────────────────────────────────────────────

export type ColumnVisibilityModel = Record<string, boolean>;

interface DataGridPrefsState {
	/** Column visibility per grid: `{ rules: { description: false }, ... }`. */
	columnVisibility: Record<string, ColumnVisibilityModel>;
}

const initialState: DataGridPrefsState = {
	columnVisibility: {}
};

export const dataGridPrefsSlice = createSlice({
	name: "dataGridPrefs",
	initialState,
	reducers: {
		setGridColumnVisibility: (
			state,
			action: PayloadAction<{ gridKey: string; model: ColumnVisibilityModel }>
		) => {
			state.columnVisibility[action.payload.gridKey] = action.payload.model;
		},
		toggleGridColumnVisibility: (
			state,
			action: PayloadAction<{ gridKey: string; field: string }>
		) => {
			const { gridKey, field } = action.payload;
			const current = state.columnVisibility[gridKey] ?? {};
			// `false` means hidden — toggling moves between {false} and {} (visible).
			const isVisible = current[field] !== false;
			state.columnVisibility[gridKey] = { ...current, [field]: !isVisible };
		},
		resetGridColumnVisibility: (state, action: PayloadAction<{ gridKey: string }>) => {
			delete state.columnVisibility[action.payload.gridKey];
		}
	}
});

interface RootSelectorState {
	root: {
		dataGridPrefs?: DataGridPrefsState;
	};
}

export const selectColumnVisibilityForGrid = (gridKey: string) => (state: RootSelectorState) =>
	state.root.dataGridPrefs?.columnVisibility?.[gridKey] ?? {};

export const {
	setGridColumnVisibility,
	toggleGridColumnVisibility,
	resetGridColumnVisibility
} = dataGridPrefsSlice.actions;

export default dataGridPrefsSlice.reducer;
